import { AlertCircle, ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { recoverFromStuckAuth } from '@/auth/recoverSession'

interface AuthLoadingTimeoutProps {
  title?: string
  description?: string
  /**
   * Identifies the surface that gave up (authenticated layout, callback,
   * login). Passed through to the recovery navigation so /login can show
   * a contextual banner — and so we can grep telemetry later.
   */
  reason?: string
}

/**
 * Recovery card rendered when {@link useAuthLoadingWatchdog} decides that
 * Logto's `isLoading` state has been stuck for too long to be a normal
 * rehydrate. Replaces a forever-spinner with an explicit, user-actionable
 * step: clear the cached session and start the sign-in flow from scratch.
 *
 * Intentionally NOT auto-triggered. A genuinely-slow rehydrate that
 * resolves at second 16 would otherwise look like a redirect storm; a
 * single button keeps the recovery deterministic and recoverable in turn.
 */
export function AuthLoadingTimeout({
  title = 'Sign-in is taking too long',
  description = "We couldn't restore your session. This usually fixes itself after a fresh sign-in.",
  reason = 'session_timeout',
}: AuthLoadingTimeoutProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div
            aria-hidden
            className="mx-auto mb-2 flex size-10 items-center justify-center rounded-full bg-amber-500/10 text-amber-600"
          >
            <AlertCircle className="size-5" />
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-stretch gap-2">
          <Button onClick={() => recoverFromStuckAuth(reason)}>
            Sign in again
            <ArrowRight />
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
